import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { DEFAULT_CONTENT } from "./defaults";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const Ctx = createContext({ content: DEFAULT_CONTENT, reload: () => {}, lang: "fr", setLang: () => {} });

export const resolveSrc = (src) => (typeof src === "string" && src.startsWith("/api/") ? `${BACKEND}${src}` : src || "");

const normalize = (raw) => {
  if (!raw?.profile || !Array.isArray(raw?.sections)) return DEFAULT_CONTENT;
  return { profile: { ...DEFAULT_CONTENT.profile, ...raw.profile }, sections: raw.sections.filter((s) => s?.id && s?.type) };
};

const initialLang = () => {
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  if (fromUrl === "en" || fromUrl === "fr") return fromUrl;
  try {
    return localStorage.getItem("lang") === "en" ? "en" : "fr";
  } catch {
    return "fr";
  }
};

const fetchContent = async (lang) => {
  const { data } = await axios.get(`${BACKEND}/api/content`, { params: { lang } });
  return data;
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [lang, setLangState] = useState(initialLang);

  const setLang = useCallback((l) => {
    setLangState(l);
    try { localStorage.setItem("lang", l); } catch { /* stockage indisponible */ }
  }, []);

  const reload = useCallback(async () => {
    if (!BACKEND) return;
    try {
      let data = await fetchContent(lang);
      if (lang === "en" && !data?.profile) data = await fetchContent("fr");
      setContent(normalize(data));
    } catch {
      setContent(DEFAULT_CONTENT);
    }
  }, [lang]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  return <Ctx.Provider value={{ content, reload, lang, setLang }}>{children}</Ctx.Provider>;
};

export const useContent = () => useContext(Ctx).content;

export const useContentReload = () => useContext(Ctx).reload;

export const useLang = () => {
  const { lang, setLang } = useContext(Ctx);
  return { lang, setLang };
};

export const normalizeContent = (raw) => normalize(raw);

export const useVisibleSections = () => useContent().sections.filter((s) => s.visible !== false);

export const useSection = (id) => useContent().sections.find((s) => s.id === id);
